
import React, { useState, useEffect } from 'react';
import Modal from './Modal';

const MoveDeviceModal = ({ isOpen, onClose, device, sites, onMove }) => {
        const [selectedSiteId, setSelectedSiteId] = useState('');
        const [selectedRoomId, setSelectedRoomId] = useState('');
        const [roomsInSelectedSite, setRoomsInSelectedSite] = useState([]);

        // With a single site there is nothing to choose: the site picker is hidden
        // and moving a device is purely a room choice.
        const isMultiSite = (sites?.length || 0) > 1;

        useEffect(() => {
                if (device && sites) {
                        const currentRoom = sites.flatMap(s => s.rooms).find(r => r.devices.some(d => d.id === device.id));
                        if (currentRoom) {
                                const currentSite = sites.find(s => s.id === currentRoom.site_id);
                                if (currentSite) {
                                        setSelectedSiteId(currentSite.id);
                                        setRoomsInSelectedSite(currentSite.rooms);
                                        setSelectedRoomId(currentRoom.id);
                                }
                        }
                }
        }, [device, sites]);

        // Single-site customers never see the picker, so the only site is always
        // the selected one — even if the device's current room can't be resolved.
        useEffect(() => {
                if (!isMultiSite && sites?.length === 1) {
                        setSelectedSiteId(sites[0].id);
                }
        }, [isMultiSite, sites]);

        useEffect(() => {
                if (selectedSiteId) {
                        const site = sites.find(s => s.id === parseInt(selectedSiteId));
                        setRoomsInSelectedSite(site ? site.rooms : []);
                        setSelectedRoomId('');
                } else {
                        setRoomsInSelectedSite([]);
                        setSelectedRoomId('');
                }
        }, [selectedSiteId, sites]);

        const handleMove = () => {
                if (selectedRoomId) {
                        onMove(device.id, selectedRoomId, device.name);
                }
        };

        if (!device) {
                return null;
        }

        return (
                <Modal isOpen={isOpen} onClose={onClose}>
                        <h3 className="text-lg font-bold mb-4">Move Device</h3>
                        <div className="space-y-4">
                                {isMultiSite && (
                                        <div>
                                                <label htmlFor="site-select" className="block text-sm font-medium text-gray-700">
                                                        Site
                                                </label>
                                                <select
                                                        id="site-select"
                                                        data-testid="move-site"
                                                        value={selectedSiteId}
                                                        onChange={(e) => setSelectedSiteId(e.target.value)}
                                                        className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-brand focus:border-brand sm:text-sm rounded-md"
                                                >
                                                        <option value="">Select a site</option>
                                                        {sites.map(site => (
                                                                <option key={site.id} value={site.id}>
                                                                        {site.name}
                                                                </option>
                                                        ))}
                                                </select>
                                        </div>
                                )}
                                <div>
                                        <label htmlFor="room-select" className="block text-sm font-medium text-gray-700">
                                                Room
                                        </label>
                                        <select
                                                id="room-select"
                                                data-testid="move-room"
                                                value={selectedRoomId}
                                                onChange={(e) => setSelectedRoomId(e.target.value)}
                                                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-brand focus:border-brand sm:text-sm rounded-md"
                                                disabled={!selectedSiteId}
                                        >
                                                <option value="">Select a room</option>
                                                {roomsInSelectedSite.map(room => (
                                                        <option key={room.id} value={room.id}>
                                                                {room.name}
                                                        </option>
                                                ))}
                                        </select>
                                </div>
                        </div>
                        <div className="mt-6 flex justify-end space-x-4">
                                <button
                                        type="button"
                                        onClick={onClose}
                                        data-testid="move-cancel"
                                        className="inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:text-sm"
                                >
                                        Cancel
                                </button>
                                <button
                                        type="button"
                                        onClick={handleMove}
                                        data-testid="move-submit"
                                        disabled={!selectedRoomId}
                                        className="inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-brand text-base font-medium text-white hover:bg-brand/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand sm:text-sm disabled:bg-gray-300"
                                >
                                        Move
                                </button>
                        </div>
                </Modal>
        );
};

export default MoveDeviceModal;
